import React, {useState, useEffect, useRef} from 'react';
import {ContentDispatch} from 'framework';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {TabComponent} from 'componentLibrary/modules/TabComponent';
import REST from 'go/requests/rest';
import {ApplicationsTable} from './ApplicationsTable';
import {getAppActions, compileContent, isRecipientUser} from 'common/utils';
import {Pagination, Loader, Button, Input, Icon, Header} from 'semantic-ui-react';
import {extractOrgIdsFromPermissions} from 'go/security/permissions';
import {withRouter} from 'react-router-dom';
import queryString from 'query-string';
import _ from 'lodash';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const ApplicationsTableWrapper = (props) => {
  const {tabs, fundingOpportunityId, programId} = props;
  const pageSize = _.get(props, 'pageSize', 10);
  const defaultSort = _.get(props, 'defaultSort', 'statusTimeStamp');
  const defaultSortDirection = _.get(props, 'defaultSortDirection', 'ASC');
  const {header, headerSize, headerAggregate} = props;
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [requestQueue, setRequestQueue] = useState([]);
  const query = queryString.parse(_.get(props, 'location.search'));
  const [activeTabId, setActiveTabId] = useState(
    _.get(query, 'initialTab', _.get(tabs, '0.id'))
  );
  const [tabData, setTabData] = useState({});
  const tabsWithCount = _.map(tabs, (tab) => {
    const id = tab.id;
    return {
      ...tab,
      count: _.get(tabData, [id, 'count'], 0),
    };
  });
  const tab = _.find(tabsWithCount, (tab) => tab.id === activeTabId);
  const summary = _.get(tab, 'name', 'Applications') + ' table';
  const items = _.get(tabData, [activeTabId, 'items'], []);
  const tableProps = _.get(tab, 'tableProps', {});

  const [selectedColumn, setSelectedColumn] = useState(
    _.get(tableProps, 'defaultSort', defaultSort)
  );
  const [sortDirection, setSortDirection] = useState(
    _.get(tableProps, 'defaultSortDirection', defaultSortDirection)
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isClearingSearchTerm, setIsClearingSearchTerm] = useState(false);
  const [tableHeight, setTableHeight] = useState('63px');
  const prevSearchTerm = usePrevious(searchTerm);
  const tableRef = useRef(null);
  const requestQueueRef = useRef();
  requestQueueRef.current = requestQueue;

  const activeTabFilteredCount = _.get(tabData, [activeTabId, 'filteredCount'], 0);
  const totalPages = Math.ceil(activeTabFilteredCount / pageSize);
  let showPrevButton = currentPage + 1 > 1 ? undefined : null;
  let showNextButton = currentPage + 1 < totalPages ? undefined : null;

  const setActiveTab = (tabId) => {
    setActiveTabId(tabId);
    const activeTab = _.find(tabs, (tab) => tab.id === tabId);
    const tabDefaultSort = _.get(activeTab, 'tableProps.defaultSort', defaultSort);
    const tabDefaultSortDirection = _.get(
      activeTab,
      'tableProps.defaultSortDirection',
      defaultSortDirection
    );
    setSelectedColumn(tabDefaultSort);
    setSortDirection(tabDefaultSortDirection);
    setCurrentPage(0);
    setSearchTerm('');
  };

  const setSelectedColumnProp = (columnName) => {
    setSelectedColumn(columnName);
  };

  const setSortDirectionProp = (direction) => {
    setSortDirection(direction);
    setCurrentPage(0);
  };

  const handlePageChange = (e, {activePage}) => {
    setCurrentPage(activePage - 1);
  };

  const handleTermChange = (e, entry) => {
    setSearchTerm(entry.value);
    setCurrentPage(0);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    getTabContents(activeTabId);
  };

  const onClearSearchTerm = () => {
    setIsClearingSearchTerm(true);
    setSearchTerm('');
  };

  useEffect(() => {
    getTabContents(activeTabId);
  }, []);

  useEffect(() => {
    if (!isInitialLoad) getTabContents(activeTabId);
  }, [selectedColumn, sortDirection, currentPage, activeTabId]);

  useEffect(() => {
    if (prevSearchTerm !== searchTerm && !isInitialLoad) {
      if (isClearingSearchTerm) {
        getTabContents(activeTabId);
        setIsClearingSearchTerm(false);
      } else {
        const timer = setTimeout(() => {
          getTabContents(activeTabId);
        }, 1200);

        return () => clearTimeout(timer);
      }
    }
  }, [prevSearchTerm, searchTerm, isClearingSearchTerm]);

  const getTabContents = (activeTab) => {
    setLoading(true);
    setRequestQueue([
      ...requestQueue,
      {
        type: 'getTabContentsAction',
        payload: {
          activeTab,
        },
      },
    ]);
  };

  useEffect(() => {
    if (requestQueue.length && !requestQueue[0].loading) {
      const action = {
        ...requestQueue[0],
      };
      action.loading = true;
      setRequestQueue([action, ...requestQueue.slice(1)]);
      const cb = () => {
        setRequestQueue(requestQueueRef.current.slice(1));
      };
      switch (action.type) {
        case 'getTabContentsAction':
          getTabContentsAction(action.payload.activeTab, cb, cb);
          break;
      }
    }
  }, [requestQueue]);

  const getTabContentsAction = (activeTab, cb, errorcb) => {
    setLoading(true);
    const limit = pageSize;
    const order = sortDirection;
    const offset = currentPage * pageSize;
    const sort = selectedColumn;
    const term = searchTerm;
    let appRequests = [];

    if (_.get(tableRef, 'current')) {
      let height = tableRef.current.getBoundingClientRect().height;
      setTableHeight(height);
    }

    // iterate over tabs to retrieve app data
    _.forEach(tabs, (tab) => {
      const {id, statuses, tableProps} = tab;
      const tabEntityType = _.get(tableProps, 'entityType');
      const hideColumns = _.get(tableProps, 'hideColumns', []);
      const hideColumnsString = _.join(hideColumns, ',');
      if ((activeTab === id || isInitialLoad) && _.size(statuses) > 0) {
        const statusString = _.join(statuses, ',');

        appRequests.push(
          getApplicationData(
            id,
            fundingOpportunityId,
            programId,
            limit,
            order,
            offset,
            statusString,
            sort,
            term,
            hideColumnsString,
            tabEntityType
          )
        );
      }
    });

    Promise.all(appRequests)
      .then((appResults) => {
        let newTabData = _.cloneDeep(tabData);
        _.forEach(appResults, (appData) => {
          let tabName = appData.tabName;
          newTabData[tabName] = appData;
        });
        setTabData(newTabData);
        setLoading(false);
        if (isInitialLoad) setIsInitialLoad(false);
        cb();
      })
      .catch((e) => {
        console.error('Error retrieving apps');
        errorcb();
      });
  };

  const getApplicationData = (
    tabName,
    fundingOpportunityId,
    programId,
    limit,
    order,
    offset,
    statuses,
    sort,
    term,
    hideColumns,
    tabEntityType
  ) => {
    let permission = props.permissions;
    let userOrg;
    let userOrgs;
    if (isRecipientUser(props.permissions)) {
      userOrgs = extractOrgIdsFromPermissions(props.permissions);
      userOrg = userOrgs[0];
    }
    let selector = userOrg
      ? 'get_paginated_entities_for_funding_opportunity_by_orgids'
      : 'get_paginated_entities_for_funding_opportunity';
    return REST.search
      .getPaginatedEntitiesForFo(
        tabEntityType,
        fundingOpportunityId,
        programId,
        limit,
        order,
        offset,
        statuses,
        sort,
        term,
        hideColumns,
        userOrg,
        userOrgs,
        selector
      )
      .then(REST.handler.handleResponse)
      .then((data) => {
        const count = term
          ? _.get(tabData, [activeTabId, 'count'])
          : _.get(data, 'items[0].inner_hits.totalCount', 0);
        const filteredCount = _.get(data, 'items[0].inner_hits.filteredCount', 0);
        const items = _.map(_.get(data, 'items', []), (app) => {
          const entitiesEnabled = _.get(props, 'entitiesEnabled', []);
          const actions = getAppActions(
            tabEntityType,
            entitiesEnabled,
            tabName,
            permission
          );
          return {
            ...app,
            actions,
          };
        });
        const appData = {
          tabName,
          count,
          filteredCount,
          items,
        };

        return appData;
      })
      .catch((e) => {
        console.error('Error retrieving apps');
        return Promise.reject(e);
      });
  };

  const renderHeader = () => {
    let headerResult = header;
    if (headerAggregate) {
      let totalCount = 0;
      _.forEach(tabsWithCount, (tab) => {
        totalCount += tab.count;
      });
      headerResult = header + ' (' + totalCount + ')';
    }
    //id={header} needed for sideNav locators to work for onClick navigation
    return (
      <Header
        as={headerSize || 'h3'}
        id={header}
        content={compileContent(headerResult, props)}
      />
    );
  };

  const renderAdditionalContents = (activeTab, tabs) => {
    const tabData = _.find(tabs, (data) => data.id === activeTab);
    const contents = _.get(tabData, 'contents');

    if (!contents) {
      return null;
    } else {
      return contents.map((content, contentIndex) => {
        return content ? <ContentDispatch key={contentIndex} config={content} /> : null;
      });
    }
  };

  const doEntityAction = (action, entity) => {
    const {redirectNewTab} = props;
    const {grantId, entityId} = entity;
    switch (action) {
      case 'viewGrant':
        if (redirectNewTab) window.open(`/grant/${grantId}`, '_blank');
        else props.history.push(`/grant/${grantId}`);
        break;
      case 'viewApplication':
        if (redirectNewTab) window.open(`/grant/${grantId}/application`, '_blank');
        else props.history.push(`/grant/${grantId}/application`);
        break;
      case 'continueApplication':
        if (redirectNewTab) window.open(`/application/${entityId}/edit`, '_blank');
        else props.history.push(`/application/${entityId}/edit`);
        break;
      default:
        console.error('Unsupported action');
    }
  };

  return (
    <>
      {header ? renderHeader() : null}
      <TabComponent
        tabs={tabsWithCount}
        setActiveTab={setActiveTab}
        initialTab={activeTabId}
      />
      {renderAdditionalContents(activeTabId, tabsWithCount)}
      <div className="search-bar">
        <form id="search-form" onSubmit={onSearch}>
          <span>
            <Input>
              <Input
                aria-label={`Search by term`}
                title={`Search by term`}
                value={searchTerm}
                onChange={handleTermChange}
                onFocus={(e) => e.target.select()}
                style={{width: '380px'}}
              />
              <Button primary icon aria-label="Search Button" title="Search button">
                <Icon name="search" fitted />
              </Button>
            </Input>
            {searchTerm !== '' && (
              <a
                id="clear-search"
                href="#"
                className="padding-left"
                onClick={() => onClearSearchTerm()}
              >
                Clear search
              </a>
            )}
          </span>
        </form>
      </div>
      {loading ? (
        <div
          className="margin-top"
          style={{minHeight: tableHeight, display: 'flex', alignItems: 'center'}}
        >
          <Loader inline="centered" size="large" active>
            Loading...
          </Loader>
        </div>
      ) : (
        <>
          <div ref={tableRef}>
            <ApplicationsTable
              applications={items}
              setSelectedColumnProp={setSelectedColumnProp}
              setSortDirectionProp={setSortDirectionProp}
              selectedColumn={selectedColumn}
              sortDirection={sortDirection}
              summary={summary}
              doEntityAction={doEntityAction}
              {...tableProps}
            />
            {_.size(items) < 1 && (
              <div id="no-apps-found" className="text-align-center">
                There are no applications available
              </div>
            )}

            {activeTabFilteredCount > pageSize && (
              <Pagination
                activePage={currentPage + 1}
                siblingRange={1}
                boundaryRange={1}
                prevItem={showPrevButton}
                nextItem={showNextButton}
                firstItem={showPrevButton}
                lastItem={showNextButton}
                ellipsisItem={undefined}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

const ApplicationsTableWrapperWithRouter = withRouter(ApplicationsTableWrapper);

export default connectDynamic(ApplicationsTableWrapperWithRouter);
